/**
 * The assistant wraps song lists in ```songlist code blocks, one
 * "Artist - Title" per line. Screenshots keep photo order and one block per
 * section ("```songlist Must Play"). Typed crate requests use one block with
 * the crate name ("```songlist 2010 Bangers") already in playable order.
 */

export type SongListSegment =
  | { type: 'markdown'; text: string }
  | { type: 'songlist'; raw: string; label?: string };

const CLOSED_FENCE = /```songlist([^\n]*)\n([\s\S]*?)```/g;
const OPEN_FENCE = /```songlist([^\n]*)\n([\s\S]*)$/;
/** Closing backticks of a fence that is still arriving token by token. */
const TRAILING_BACKTICKS = /\n?`{1,3}$/;
/** An opening fence caught mid-word or mid-label, e.g. "```songlist Must P". */
const PARTIAL_FENCE_TAIL =
  /\n?`{1,3}(?:songlist[^\n]*|songlis|songli|songl|song|son|so|s)?$/;

const cleanLabel = (raw: string): string | undefined => {
  const label = raw.replace(/^[\s:·–—-]+|[\s:·–—-]+$/g, '').trim();
  return label || undefined;
};

export const splitSongListSegments = (
  content: string,
  streaming = false,
): SongListSegment[] => {
  const segments: SongListSegment[] = [];
  let cursor = 0;
  const fence = new RegExp(CLOSED_FENCE.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = fence.exec(content)) !== null) {
    const before = content.slice(cursor, match.index).trim();
    if (before) segments.push({ type: 'markdown', text: before });
    segments.push({
      type: 'songlist',
      raw: match[2],
      label: cleanLabel(match[1]),
    });
    cursor = match.index + match[0].length;
  }

  const rest = content.slice(cursor);
  // While the reply streams in, the closing fence has not arrived yet.
  const open = rest.match(OPEN_FENCE);
  if (open && open.index !== undefined) {
    const before = rest.slice(0, open.index).trim();
    if (before) segments.push({ type: 'markdown', text: before });
    segments.push({
      type: 'songlist',
      raw: open[2].replace(TRAILING_BACKTICKS, ''),
      label: cleanLabel(open[1]),
    });
  } else {
    // Mid-stream, a half-arrived "```songlist Must P" would flash as literal
    // backticks; trim it and let the next token re-split the full text.
    const text = streaming ? rest.replace(PARTIAL_FENCE_TAIL, '') : rest;
    if (text.trim()) segments.push({ type: 'markdown', text: text.trim() });
  }

  return segments;
};

export type ExtractedSong = {
  artist: string;
  title: string;
  /** The assistant was not fully sure of its reading of this line. */
  uncertain: boolean;
};

const LEADING_MARKER = /^\s*(?:\d+[.)]\s*|[-•*]\s+)?/;
const ARTIST_TITLE_SPLIT = /\s+[—–-]\s+/;
const UNCERTAIN_SUFFIX = /\s*\(\?\)\s*$/;

export const parseSongList = (raw: string): ExtractedSong[] =>
  raw
    .split('\n')
    .map((line) => line.replace(LEADING_MARKER, '').trim())
    .filter(Boolean)
    .map((line) => {
      const uncertain = UNCERTAIN_SUFFIX.test(line);
      const cleaned = line.replace(UNCERTAIN_SUFFIX, '').trim();
      const parts = cleaned.split(ARTIST_TITLE_SPLIT);
      if (parts.length >= 2) {
        return {
          artist: parts[0].trim(),
          title: parts.slice(1).join(' — ').trim(),
          uncertain,
        };
      }
      return { artist: '', title: cleaned, uncertain };
    });
